import React, { useMemo } from 'react';
import { diffLines } from 'diff';

interface PlanConflictDialogProps {
  mine: string;
  theirs: string;
  onKeepMine: () => void;
  onUseTheirs: () => void;
}

export default function PlanConflictDialog({ mine, theirs, onKeepMine, onUseTheirs }: PlanConflictDialogProps) {
  const changes = useMemo(() => diffLines(mine, theirs), [mine, theirs]);

  // Build left (mine) and right (theirs) line arrays with diff markers
  const { leftLines, rightLines } = useMemo(() => {
    const left: { text: string; type: 'same' | 'removed' | 'empty' }[] = [];
    const right: { text: string; type: 'same' | 'added' | 'empty' }[] = [];

    for (const part of changes) {
      const lines = part.value.replace(/\n$/, '').split('\n');
      if (!part.added && !part.removed) {
        // Unchanged
        for (const line of lines) {
          left.push({ text: line, type: 'same' });
          right.push({ text: line, type: 'same' });
        }
      } else if (part.removed) {
        for (const line of lines) {
          left.push({ text: line, type: 'removed' });
          right.push({ text: '', type: 'empty' });
        }
      } else if (part.added) {
        for (const line of lines) {
          left.push({ text: '', type: 'empty' });
          right.push({ text: line, type: 'added' });
        }
      }
    }
    return { leftLines: left, rightLines: right };
  }, [changes]);

  const lineStyle = (type: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      padding: '1px 8px',
      fontSize: 'var(--fs-sm)',
      fontFamily: 'monospace',
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      minHeight: '1.6em',
    };
    if (type === 'removed') return { ...base, background: 'rgba(239,68,68,0.12)', color: '#b91c1c' };
    if (type === 'added') return { ...base, background: 'rgba(34,197,94,0.12)', color: '#15803d' };
    if (type === 'empty') return { ...base, background: 'var(--code-bg)', opacity: 0.3 };
    return { ...base, color: 'var(--text-secondary)' };
  };

  const hasChanges = changes.some(c => c.added || c.removed);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
    }}>
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 16, padding: '24px 28px',
        maxWidth: 900, width: '95%', maxHeight: '80vh',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)', border: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
      }}>
        <h3 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
          Plan Updated by Agent
        </h3>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginBottom: 16 }}>
          {hasChanges
            ? 'The agent generated a new version while you were editing. Differences are highlighted below.'
            : 'The agent generated a version identical to yours.'}
        </p>

        {/* Diff view */}
        {hasChanges && (
          <div style={{
            flex: 1, minHeight: 0, display: 'flex', gap: 1,
            border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
            marginBottom: 16,
          }}>
            {/* Left: mine */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{
                padding: '6px 10px', background: 'rgba(239,68,68,0.06)',
                borderBottom: '1px solid var(--border)', flexShrink: 0,
              }}>
                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: '#b91c1c' }}>My Version</span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {leftLines.map((line, i) => (
                  <div key={i} style={lineStyle(line.type)}>
                    {line.text || '\u00A0'}
                  </div>
                ))}
              </div>
            </div>
            {/* Divider */}
            <div style={{ width: 1, background: 'var(--border)', flexShrink: 0 }} />
            {/* Right: theirs */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{
                padding: '6px 10px', background: 'rgba(34,197,94,0.06)',
                borderBottom: '1px solid var(--border)', flexShrink: 0,
              }}>
                <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: '#15803d' }}>Agent's Version</span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {rightLines.map((line, i) => (
                  <div key={i} style={lineStyle(line.type)}>
                    {line.text || '\u00A0'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onKeepMine}
            style={{
              padding: '8px 16px', fontSize: 'var(--fs-sm)', fontWeight: 500,
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
              color: 'var(--text-secondary)',
            }}
          >
            Keep My Version
          </button>
          <button
            onClick={onUseTheirs}
            style={{
              padding: '8px 16px', fontSize: 'var(--fs-sm)', fontWeight: 600,
              background: '#0033FF', color: 'white', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Use Agent's Version
          </button>
        </div>
      </div>
    </div>
  );
}
